// lib
import React, { useCallback, useRef, useState } from 'react'
import { useParams } from 'react-router'

// api
import {
  MeetingFragmentFragment,
  MeetingSortingKeys,
  ProjectType,
  SortDirection,
  useMeetings
} from '@lopo-tech/lopo-api'

// components
import HeaderProject from 'src/components/header/subheaders/pages/project_detail/header_project'
import LoadingSpinner from 'src/components/loading_spinner/loading_spinner'
import MetaTags from 'src/components/meta_tags/meta_tags'
import { ProjectNotFound } from 'src/components/projects/not_found/project_not_found'
import { DetailCard } from 'src/components/projects/project_detail/detail_card/detail_card'
import { CLASS_PREFIX } from 'src/constants'

// layout
import {
  DETAILS_PAGE_TITLE,
  INFRASTRUCTURE_PROJECTSFEED_DESCRIPTION,
  PROJECTSFEED_DESCRIPTION
} from 'src/constants/meta_tags'
import { getProjectRoutes } from 'src/constants/urls'
import ListWrapper from 'src/containers/smart_components/list_wrapper/list_wrapper'
import StandardPageTitle from 'src/containers/smart_components/page_title/page_title'
import LayoutNoMap from 'src/layout/layout_no_map'
import { useGetAppLocationShape } from 'src/reducers/app-state/app-state_hooks'

import { Button } from 'src/components/button/button'
import Icon from 'src/components/forms/icon/icon'
import { MeetingTile } from 'src/components/projects/meeting/meeting_tile'
import { ProjectCreateMeetingModal } from 'src/components/projects/project_create_meeting/project_create_meeting_modal'
import { ProjectCreateReportModal } from 'src/components/projects/report/create_report_modal'
import { textResources } from 'src/lang/de'
import { COLOR_TYPE_WHITE } from 'src/utils/color'
import './project_meetings.scss'

const t = textResources.projects
const cls = `${CLASS_PREFIX}project-meetings`

const PER_PAGE = 10

interface IProps {
  projectType: ProjectType;
}

export const ProjectMeetingsPage: React.FC<IProps> = ({ projectType }) => {
  const routes = getProjectRoutes(projectType)
  const { slug } = useParams<{ slug: string }>()
  const locationShape = useGetAppLocationShape()
  const isInfrastructureProject = projectType === ProjectType.InfrastructureProject

  const { data, loading, error, fetchMore, refetch } = useMeetings({
    variables: {
      slug,
      page: 1,
      perPage: PER_PAGE,
      sorting: {
        direction: SortDirection.Desc,
        fieldName: MeetingSortingKeys.StartTime
      }
    }
  })

  const projectName = data?.project?.name || t.details.nameFallback

  const loadingRef = useRef(loading)
  loadingRef.current = loading

  const [meetingForReport, setMeetingForReport] =
    useState<MeetingFragmentFragment | null>(null)

  const pageInfo = data?.project?.meetings?.pageInfo

  const getNextPage = useCallback(() => {
    if (pageInfo?.nextPage && !loadingRef.current) {
      fetchMore({ variables: { page: pageInfo.nextPage } })
    }
  }, [fetchMore, pageInfo?.nextPage])

  const isInitialLoading = loading && !data

  const [editingMeeting, setEditingMeeting] =
    useState<MeetingFragmentFragment | null>(null)

  const [isCreateMeetingOpen, setIsCreateMeetingOpen] = useState(false)

  if (isInitialLoading) {
    return <LoadingSpinner shown={true} />
  }

  const project = data?.project
  const isNotFound =
    Boolean(error) || !project || !project.canShowMeetings.value

  if (isNotFound) {
    return <ProjectNotFound />
  }
  const meetings = project.meetings?.items ?? []

  const descriptionResolver = isInfrastructureProject ? INFRASTRUCTURE_PROJECTSFEED_DESCRIPTION : PROJECTSFEED_DESCRIPTION
  const description = locationShape ? descriptionResolver(locationShape.name) : undefined
  const title = DETAILS_PAGE_TITLE(projectName)

  return (
    <>
      <MetaTags
        description={description}
        geo_placename={locationShape?.name}
        geo_position={
          locationShape &&
          `${locationShape.center.latitude};${locationShape.center.longitude}`
        }
        og_url={document.location.toString()}
        title={title}
      />
      <LayoutNoMap
        subHeader={
          <HeaderProject
            title={projectName}
            fallbackRoute={routes.detail(slug)}
            showMoreButton={false}
          />
        }
        pageTitle={
          <StandardPageTitle
            text={isInfrastructureProject ? t.common.infrastructureProjects : t.common.projects}
            showLocation
          />
        }
      >
        <ListWrapper getNextPage={getNextPage}>
          <div className={cls}>
            <div className={`${cls}__header`}>
              <h2 className={`${cls}__section-title`}>
                {t.details.meetingSchedule}
              </h2>
              {project.canCreateMeetings?.value && (
                <Button
                  onClick={() => {
                    setEditingMeeting(null)
                    setIsCreateMeetingOpen(true)
                  }}
                  iconLeft={
                    <span className={`${cls}__create-btn-icon`} aria-hidden>
                      <Icon name="plus" size={18} color={COLOR_TYPE_WHITE} />
                    </span>
                  }
                  title={t.details.createMeeting}
                />
              )}
            </div>

            {meetings.length === 0 && (
              <DetailCard withPadding={true}>
                <div>{t.details.noMeetingsYet}</div>
              </DetailCard>
            )}

            {meetings.map((meeting) => (
              <MeetingTile
                key={meeting.id}
                meeting={meeting}
                onEdit={() => {
                  setEditingMeeting(meeting)
                }}
                setMeetingForReport={setMeetingForReport}
              />
            ))}
          </div>

          {meetingForReport && (
            <ProjectCreateReportModal
              meeting={meetingForReport}
              closeModal={() => setMeetingForReport(null)}
            />
          )}

          {editingMeeting && (
            <ProjectCreateMeetingModal
              projectId={project.id}
              prefill={editingMeeting}
              onClose={() => setEditingMeeting(null)}
              onCreated={() => {
                setEditingMeeting(null)
                refetch()
              }}
            />
          )}

          {isCreateMeetingOpen && (
            <ProjectCreateMeetingModal
              projectId={project.id}
              onClose={() => setIsCreateMeetingOpen(false)}
              onCreated={() => {
                setIsCreateMeetingOpen(false)
                refetch()
              }}
            />
          )}
        </ListWrapper>
      </LayoutNoMap>
    </>
  )
}

const PublicParticipationProjectMeetingsPage: React.FC = () => (
  <ProjectMeetingsPage projectType={ProjectType.PublicParticipationProject} />
)

export default PublicParticipationProjectMeetingsPage
